import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthPageShell, OrganizationCard } from '@/components';
import { useAuthFetch } from '@/hooks';
import type { ConversationSummary, DoctorListItem } from '@dermalink/shared';
import { practicePath } from '../../routes.ts';
import styles from './PracticePicker.module.css';

interface PracticeOption {
  slug: string;
  name: string;
  doctorCount: number;
  availableCount: number;
  consultCount: number;
  unreadCount: number;
  lastActivity: string | null;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Patient entry point: every patient page is scoped to one practice, so the patient picks one
 * here. Practices link straight to /praktijk/:slug from their own website, skipping this page.
 */
export function PracticePicker() {
  const navigate  = useNavigate();
  const authFetch = useAuthFetch();

  const doctorsQuery = useQuery<DoctorListItem[]>({
    queryKey: ['doctors'],
    queryFn: async () => {
      const res = await authFetch('/doctors');
      if (!res.ok) throw new Error();
      return res.json();
    },
  });
  const conversationsQuery = useQuery<ConversationSummary[]>({
    queryKey: ['patient-conversations'],
    queryFn: async () => {
      const res = await authFetch('/conversations');
      if (!res.ok) throw new Error();
      return res.json();
    },
  });

  const isPending = doctorsQuery.isPending || conversationsQuery.isPending;
  const isError   = doctorsQuery.isError || conversationsQuery.isError;

  const bySlug = new Map<string, PracticeOption>();
  function option(slug: string, name: string): PracticeOption {
    const existing = bySlug.get(slug);
    if (existing) return existing;
    const created = { slug, name, doctorCount: 0, availableCount: 0, consultCount: 0, unreadCount: 0, lastActivity: null };
    bySlug.set(slug, created);
    return created;
  }
  for (const doc of doctorsQuery.data ?? []) {
    if (!doc.organization_slug || !doc.organization_name) continue;
    const o = option(doc.organization_slug, doc.organization_name);
    o.doctorCount++;
    if (doc.is_available) o.availableCount++;
  }
  // Practices the patient consulted stay listed even without doctors today, so their history stays reachable.
  for (const conv of conversationsQuery.data ?? []) {
    if (!conv.doctor_organisation_slug || !conv.doctor_organisation) continue;
    const o = option(conv.doctor_organisation_slug, conv.doctor_organisation);
    o.consultCount++;
    o.unreadCount += conv.unread_count;
    const activity = conv.last_message_at ?? conv.created_at;
    if (!o.lastActivity || activity > o.lastActivity) o.lastActivity = activity;
  }
  const practices = [...bySlug.values()].sort((a, b) =>
    (b.lastActivity ?? '').localeCompare(a.lastActivity ?? '') || a.name.localeCompare(b.name, 'nl'));

  if (!isPending && !isError && practices.length === 1) {
    return <Navigate to={practicePath(practices[0].slug)} replace />;
  }

  return (
    <AuthPageShell maxWidth="720px" padTop="16px" patientBackdrop>
      <div className={styles.pageCard}>
        <h1 className="page-heading">Kies je praktijk</h1>
        <p className={`text-muted ${styles.subheading}`}>Bij welke praktijk wil je een teleconsultatie starten of opvolgen?</p>

        {isPending && <p className="text-muted status-message">Laden&hellip;</p>}
        {isError && <p className="text-muted status-message">Kon de praktijken niet laden.</p>}
        {!isPending && !isError && practices.length === 0 && (
          <p className="text-muted status-message">Er zijn momenteel geen praktijken beschikbaar.</p>
        )}

        <div className={styles.grid}>
          {practices.map((p) => {
            const parts = [plural(p.doctorCount, 'dermatoloog', 'dermatologen')];
            if (p.consultCount > 0) parts.push(plural(p.consultCount, 'consult', 'consulten'));
            return (
              <OrganizationCard
                key={p.slug}
                name={p.name}
                subtitle={parts.join(' · ')}
                note={p.unreadCount > 0 ? plural(p.unreadCount, 'ongelezen bericht', 'ongelezen berichten') : undefined}
                unavailable={p.availableCount === 0 && p.consultCount === 0}
                onSelect={() => navigate(practicePath(p.slug))}
              />
            );
          })}
        </div>
      </div>
    </AuthPageShell>
  );
}
